import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Chip,
  Paper,
  Snackbar,
  Tooltip,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import RouterIcon from '@mui/icons-material/Router';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SyncIcon from '@mui/icons-material/Sync';
import { ExecutionPanel, ExecutionControls } from '../execution';
import useExecutionStatus from '../../hooks/useExecutionStatus';
import useExecutionControl from '../../hooks/useExecutionControl';
import {
  useCreateTestRun,
  useTestRunsQuery,
  useCancelTestRun,
  useDatasheetPreviewQuery
} from '../../hooks/useTestRuns';
import { useQueryClient } from '@tanstack/react-query';
import {
  blockedPointHint,
  catalogIssueBanner
} from '../../utils/datasheetPreviewHints';
import {
  useStationDevicesQuery,
  useSetActiveRobot
} from '@api/bff/stationDevices';
import { useMqttDiagnostics } from '@api/bff/mqttDiagnostics';
import { hydrateMasterScriptData } from '../../types/steps';
import { DEFAULT_CATALOG } from '../../data/defaultCatalog';
import { EquipmentPicker } from './EquipmentPicker';
import { GroupPicker } from './GroupPicker';
import { CalibrationRunner } from '@features/calibration/CalibrationRunner';
import type { ScriptFunctionGroup } from './GroupPicker';
import type { ItemScriptData } from '../../types/steps';
import type { Script, EquipmentRef } from '../../types';

type ActiveBenchRun = {
  dcRuntimeId: string | null;
  hasEquipment: boolean;
  stationId: string | null;
};

type Props = {
  scriptId: string;
  globalId: string;
  masterScript?: Script;
  items?: Script[];
};

export const TestTab = ({
  scriptId,
  globalId: _globalId,
  masterScript,
  items
}: Props) => {
  const createTestRun = useCreateTestRun(scriptId);
  const preview = useDatasheetPreviewQuery(scriptId);
  const queryClient = useQueryClient();

  // Re-check resolvability whenever the script's items change (stub resolved,
  // item added/removed) — the preview is derived from them.
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['datasheetPreview', scriptId] });
  }, [items, scriptId, queryClient]);

  const { robots, activeStationId } = useStationDevicesQuery();
  const setActiveRobot = useSetActiveRobot();

  const cancelTestRun = useCancelTestRun(scriptId);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [selectedEquipment, setSelectedEquipment] =
    useState<EquipmentRef | null>(null);
  const [activeBenchRun, setActiveBenchRun] = useState<ActiveBenchRun | null>(
    null
  );
  const [isStarting, setIsStarting] = useState(false);
  const startingRef = useRef(false);
  const forcedResetRef = useRef(false);

  const { runStatus, itemStatus, events, measurements, reset, loadFromSaved } =
    useExecutionStatus(activeRunId);
  const {
    sendCommand,
    publishStart,
    publishPause,
    publishResume,
    publishAbort,
    publishForceReset,
    isConnected
  } = useExecutionControl(activeRunId);

  const { data: recentRuns } = useTestRunsQuery(scriptId, 1, 1);
  const restoredRunIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeRunId) return;
    if (forcedResetRef.current) return;
    const latest = recentRuns?.data?.[0];
    if (!latest) return;

    if (latest.status === 'RUNNING' || latest.status === 'PENDING') {
      setActiveRunId(latest.id);
      setActiveBenchRun({
        dcRuntimeId: latest.dcRuntimeId ?? null,
        hasEquipment: !!latest.equipmentRef,
        stationId: null
      });
      setTimeout(() => loadFromSaved(latest), 50);
    } else if (
      restoredRunIdRef.current !== latest.id &&
      (latest.status === 'COMPLETED' ||
        latest.status === 'FAILED' ||
        latest.status === 'ABORTED')
    ) {
      restoredRunIdRef.current = latest.id;
      setActiveRunId(latest.id);
      setActiveBenchRun({
        dcRuntimeId: latest.dcRuntimeId ?? null,
        hasEquipment: !!latest.equipmentRef,
        stationId: null
      });
      setTimeout(() => loadFromSaved(latest), 50);
    }
  }, [recentRuns, activeRunId, loadFromSaved]);

  const masterData = useMemo(() => {
    if (!masterScript?.scriptData)
      return { pickupSteps: [], items: [], returnSteps: [] };
    try {
      const parsed = JSON.parse(masterScript.scriptData);
      return hydrateMasterScriptData(parsed as Record<string, unknown>);
    } catch {
      return { pickupSteps: [], items: [], returnSteps: [] };
    }
  }, [masterScript?.scriptData]);

  const scriptGroups = useMemo<ScriptFunctionGroup[]>(() => {
    // Server report is authoritative once loaded: same code path as the launch gate.
    if (preview.data && preview.data.groups.length > 0) {
      return preview.data.groups.map(g => ({
        id: g.id,
        label: g.label,
        pointCount: g.pointCount,
        blockedPoints: g.resolvable
          ? undefined
          : g.blockedPoints.map(p => ({
              label: p.label,
              hint: blockedPointHint(p, preview.data?.catalogName ?? null)
            }))
      }));
    }

    // Instant-render skeleton from the items while the preview loads (no blocked info yet).
    const order: string[] = [];
    const counts = new Map<string, number>();
    for (const item of items ?? []) {
      if (!item.scriptData) continue;
      try {
        const parsed = JSON.parse(item.scriptData) as Partial<ItemScriptData>;
        const functionId = parsed.testPointRef?.functionId;
        if (!functionId) continue;
        if (!counts.has(functionId)) order.push(functionId);
        counts.set(functionId, (counts.get(functionId) ?? 0) + 1);
      } catch {
        continue;
      }
    }
    return order.map(id => ({
      id,
      label:
        DEFAULT_CATALOG.functions.find(
          f => f.id.toLowerCase() === id.toLowerCase()
        )?.name ?? id.toUpperCase(),
      pointCount: counts.get(id) ?? 0
    }));
  }, [items, preview.data]);

  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedGroupIds(
      scriptGroups.filter(g => !g.blockedPoints?.length).map(g => g.id)
    );
  }, [scriptGroups]);

  const noGroupsSelected =
    scriptGroups.length > 0 && selectedGroupIds.length === 0;

  // Fail-closed: until we know which groups can launch, don't let a run start.
  const previewGating =
    scriptGroups.length > 0 && (preview.isLoading || preview.isError);
  const blockedGroupCount = scriptGroups.filter(
    g => g.blockedPoints?.length
  ).length;

  const { backendMqttConnected, reconnect: reconnectBackendMqtt } =
    useMqttDiagnostics();

  const robot = robots.find(r => r.stationId === activeStationId) ?? robots[0];

  const isIdle =
    !activeRunId ||
    !runStatus ||
    runStatus.status === 'COMPLETED' ||
    runStatus.status === 'FAILED' ||
    runStatus.status === 'ABORTED' ||
    runStatus.status === 'REJECTED';

  useEffect(() => {
    if (runStatus) setIsStarting(false);
  }, [runStatus]);

  useEffect(() => {
    if (!isStarting) return;
    const timer = setTimeout(() => setIsStarting(false), 10_000);
    return () => clearTimeout(timer);
  }, [isStarting]);

  const runLive = createTestRun.isPending || isStarting;

  const totalItemCount = masterData.items.length;

  const benchDcRuntimeId = activeBenchRun?.dcRuntimeId ?? null;
  const benchStationId =
    activeBenchRun?.stationId ?? activeStationId ?? undefined;
  const showNoTestPointsNotice =
    !!activeBenchRun &&
    !activeBenchRun.dcRuntimeId &&
    scriptGroups.length === 0;
  const isDryRun =
    !!activeBenchRun &&
    !activeBenchRun.hasEquipment &&
    !!activeBenchRun.dcRuntimeId;
  const benchGridSlot = benchDcRuntimeId ? (
    <Box sx={{ p: 1 }}>
      <CalibrationRunner
        dcRuntimeId={benchDcRuntimeId}
        live={
          activeRunId
            ? { runId: activeRunId, station: benchStationId }
            : undefined
        }
      />
    </Box>
  ) : undefined;

  const handleStartRun = useCallback(
    (options?: { setupOnly?: boolean }) => {
      if (startingRef.current) return;
      startingRef.current = true;
      setIsStarting(true);

      const selectedGlobalIds = masterData.items.map(i => i.globalId);
      const orderedSelectedGroups =
        scriptGroups.length > 0
          ? scriptGroups
              .filter(g => selectedGroupIds.includes(g.id))
              .map(g => g.id)
          : null;

      createTestRun.mutate(
        {
          startedBy: 'system',
          selectedItemIds:
            selectedGlobalIds.length > 0
              ? JSON.stringify(selectedGlobalIds)
              : null,
          equipmentRef: selectedEquipment,
          selectedGroups: orderedSelectedGroups
        },
        {
          onSuccess: testRun => {
            forcedResetRef.current = false;
            reset();
            setSelectedGroupIds(
              scriptGroups.filter(g => !g.blockedPoints?.length).map(g => g.id)
            );
            setActiveRunId(testRun.id);
            setActiveBenchRun({
              dcRuntimeId: testRun.dcRuntimeId ?? null,
              hasEquipment: !!selectedEquipment,
              stationId: activeStationId ?? null
            });
            setTimeout(() => {
              sendCommand('START', testRun.id, {
                setupOnly: options?.setupOnly
              });
              startingRef.current = false;
            }, 500);
          },
          onError: () => {
            startingRef.current = false;
            setIsStarting(false);
          }
        }
      );
    },
    [
      createTestRun,
      reset,
      sendCommand,
      masterData.items,
      selectedEquipment,
      activeStationId,
      scriptGroups,
      selectedGroupIds
    ]
  );

  const handleResetToIdle = useCallback(() => {
    setActiveRunId(null);
    setActiveBenchRun(null);
    reset();
  }, [reset]);

  const [snackMessage, setSnackMessage] = useState<string | null>(null);

  const handleForceReset = useCallback(() => {
    publishForceReset();
    if (activeRunId) {
      cancelTestRun.mutate(activeRunId);
    }
    forcedResetRef.current = true;
    setActiveRunId(null);
    setActiveBenchRun(null);
    reset();
    setSnackMessage('Execution state reset');
  }, [publishForceReset, reset, activeRunId, cancelTestRun]);

  const handleReconnectBackend = useCallback(() => {
    reconnectBackendMqtt.mutate(undefined, {
      onSuccess: data => {
        setSnackMessage(
          data.connected
            ? 'Backend MQTT reconnected'
            : 'Reconnect attempted — backend still disconnected'
        );
      },
      onError: () => {
        setSnackMessage('Failed to reconnect backend MQTT');
      }
    });
  }, [reconnectBackendMqtt]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {isIdle ? (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                flexWrap: 'wrap'
              }}
            >
              {robots.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <RouterIcon fontSize="small" color="primary" />
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <Select
                      value={activeStationId ?? ''}
                      onChange={e => setActiveRobot.mutate(e.target.value)}
                      disabled={setActiveRobot.isPending || runLive}
                      sx={{
                        '& .MuiSelect-select': {
                          py: 0.5,
                          fontSize: '0.8125rem'
                        }
                      }}
                    >
                      {robots.map(r => (
                        <MenuItem key={r.id} value={r.stationId}>
                          {`${r.label || r.stationId} (${r.mqttDeviceId})`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              <EquipmentPicker
                value={selectedEquipment}
                onChange={setSelectedEquipment}
                disabled={runLive}
              />
              <GroupPicker
                groups={scriptGroups}
                selectedIds={selectedGroupIds}
                onChange={setSelectedGroupIds}
                disabled={runLive}
              />
              <Chip
                label={`Browser MQTT: ${isConnected ? 'Connected' : 'Disconnected'}`}
                size="small"
                color={isConnected ? 'success' : 'error'}
                variant="outlined"
              />
              <Chip
                label={`Server MQTT: ${backendMqttConnected === null ? '...' : backendMqttConnected ? 'Connected' : 'Disconnected'}`}
                size="small"
                color={
                  backendMqttConnected === null
                    ? 'default'
                    : backendMqttConnected
                      ? 'success'
                      : 'error'
                }
                variant="outlined"
              />
              {backendMqttConnected === false && (
                <Tooltip title="Reconnect backend MQTT to broker">
                  <span>
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      startIcon={<SyncIcon />}
                      onClick={handleReconnectBackend}
                      disabled={reconnectBackendMqtt.isPending}
                    >
                      {reconnectBackendMqtt.isPending
                        ? 'Reconnecting...'
                        : 'Reconnect Server'}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto', flexWrap: 'wrap' }}>
              <Tooltip title="Reset if script runs are stuck">
                <span>
                  <Button
                    variant="text"
                    color="warning"
                    size="small"
                    startIcon={<RestartAltIcon />}
                    onClick={handleForceReset}
                    disabled={!isConnected}
                  >
                    Reset
                  </Button>
                </span>
              </Tooltip>
              {activeRunId && (
                <Button variant="text" onClick={handleResetToIdle}>
                  Clear Results
                </Button>
              )}
              <Button
                variant="outlined"
                color="info"
                startIcon={<SettingsInputAntennaIcon />}
                onClick={() => handleStartRun({ setupOnly: true })}
                disabled={
                  !isConnected ||
                  createTestRun.isPending ||
                  !robot ||
                  noGroupsSelected ||
                  previewGating
                }
              >
                Setup Only
              </Button>
              <Tooltip
                title={
                  noGroupsSelected
                    ? 'Select at least one function group to start'
                    : ''
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => handleStartRun()}
                    disabled={
                      !isConnected ||
                      createTestRun.isPending ||
                      !robot ||
                      noGroupsSelected ||
                      previewGating
                    }
                  >
                    {createTestRun.isPending ? 'Creating Run...' : 'Start Run'}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {!robot && (
            <Alert severity="warning">
              No station device configured. Go to Settings &gt; Administration
              &gt; Station Devices to configure the robot.
            </Alert>
          )}

          {preview.isError && (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => preview.refetch()}
                >
                  Retry
                </Button>
              }
            >
              Couldn&apos;t check which function groups can run — starting is
              disabled.
            </Alert>
          )}

          {preview.data?.catalogIssue && (
            <Alert severity="warning">
              {catalogIssueBanner(
                preview.data.catalogIssue.reasonCode,
                preview.data.catalogIssue.detail
              )}
            </Alert>
          )}

          {blockedGroupCount > 0 && !preview.data?.catalogIssue && (
            <Alert severity="warning">
              {`${blockedGroupCount} of ${scriptGroups.length} function group${
                scriptGroups.length === 1 ? '' : 's'
              } can't run with catalog '${
                preview.data?.catalogName ?? 'unknown'
              }' — hover a ⚠ group to see how to fix it.`}
            </Alert>
          )}

          {!isConnected && (
            <Alert severity="warning">
              Browser MQTT is not connected. Cannot start a test run.
            </Alert>
          )}

          {backendMqttConnected === false && (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<SyncIcon />}
                  onClick={handleReconnectBackend}
                  disabled={reconnectBackendMqtt.isPending}
                >
                  Reconnect
                </Button>
              }
            >
              Server MQTT is disconnected — runs will stay stuck at PENDING.
            </Alert>
          )}

          {createTestRun.isError && (
            <Alert severity="error">
              Failed to create test run: {createTestRun.error.message}
            </Alert>
          )}

          {showNoTestPointsNotice && (
            <Alert severity="warning">
              This script has no test points — no datasheet was created for this
              run, so readings are not recorded. Add test points to the script
              and start a new run.
            </Alert>
          )}

          {totalItemCount > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                {totalItemCount} test point{totalItemCount !== 1 ? 's' : ''} in
                execution order
              </Typography>
            </Paper>
          )}

          {activeRunId && runStatus && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 0.5
                }}
              >
                <Typography variant="subtitle2">Last Run Results</Typography>
                {isDryRun && (
                  <Chip label="DRY RUN" size="small" color="warning" />
                )}
              </Box>
              <ExecutionPanel
                runStatus={runStatus}
                itemStatus={itemStatus}
                events={events}
                measurements={measurements}
                scriptId={scriptId}
                runId={activeRunId ?? undefined}
                measurementsSlot={benchGridSlot}
              />
            </Box>
          )}
        </>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2">
                Run: {activeRunId?.substring(0, 8)}...
              </Typography>
              {isDryRun && (
                <Chip label="DRY RUN" size="small" color="warning" />
              )}
            </Box>
            <ExecutionControls
              status={runStatus?.status ?? null}
              onStart={publishStart}
              onPause={publishPause}
              onResume={publishResume}
              onAbort={publishAbort}
              disabled={!isConnected}
            />
          </Box>
          {showNoTestPointsNotice && (
            <Alert severity="warning">
              This script has no test points — no datasheet was created for this
              run, so readings are not recorded.
            </Alert>
          )}
          <Box>
            <ExecutionPanel
              runStatus={runStatus}
              itemStatus={itemStatus}
              events={events}
              measurements={measurements}
              measurementsSlot={benchGridSlot}
            />
          </Box>
        </>
      )}
      <Snackbar
        open={!!snackMessage}
        autoHideDuration={3000}
        onClose={() => setSnackMessage(null)}
        message={snackMessage}
      />
    </Box>
  );
};
